import ModalComponent from "../../../../components/modals/ModalFormComponent";
import { formatDateAndTime } from "../../../../helpers/appHelpers";
import { EmployeeDto } from "../../../../models/employee.dto";
import {
	SalaryAdjustmentActor,
	SalaryAdjustmentDto,
	SalaryAdjustmentStatus,
	parseNotificationRecipients,
} from "../../../../models/salary-adjustment.dto";
import { useGetSalaryAdjustmentById } from "../../../../services/salary-adjustment.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import {
	ADJUSTMENT_TYPE_LABELS,
	SalaryAdjustmentStatusBadge,
	describeUpcomingProcessing,
	formatAdjustmentPayPeriod,
	formatAdjustmentValue,
} from "../data";

type SalaryAdjustmentDetailsModalProps = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	adjustment: SalaryAdjustmentDto;
	payPeriodCodes: Record<number, string>;
	employees: EmployeeDto[];
};

const formatActor = (actor: SalaryAdjustmentActor | null) =>
	actor?.name || actor?.username || "--";

const DetailItem = ({
	label,
	children,
	fullWidth,
}: {
	label: string;
	children: React.ReactNode;
	fullWidth?: boolean;
}) => (
	<div className={fullWidth ? "col-span-2" : ""}>
		<p className="text-xs font-medium text-secondary-darkgray">{label}</p>
		<div className="text-sm mt-1 break-words">{children}</div>
	</div>
);

const SalaryAdjustmentDetailsModal = ({
	open,
	setOpen,
	adjustment: initialAdjustment,
	payPeriodCodes,
	employees,
}: SalaryAdjustmentDetailsModalProps) => {
	const { currentCompany } = useCompanyStore();

	// Fetch the latest record so status changes show while the modal is open
	const { data } = useGetSalaryAdjustmentById(
		currentCompany?.id as number,
		initialAdjustment.id,
	);
	const adjustment = data?.data || initialAdjustment;
	const upcomingProcessing = describeUpcomingProcessing(
		adjustment,
		payPeriodCodes,
	);

	const recipientIds = parseNotificationRecipients(
		adjustment.notificationRecipients,
	);
	const recipientNames = recipientIds.map((id) => {
		const employee = employees.find((emp) => emp.id === id);
		return employee
			? `${employee.firstName} ${employee.lastName}`
			: `Employee #${id}`;
	});
	const reversalReportLines = (adjustment.reversalReport || "")
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean);

	return (
		<ModalComponent
			open={open}
			title="Salary Adjustment Details"
			handleClose={() => setOpen(false)}
		>
			<div className="grid grid-cols-2 gap-x-4 gap-y-5">
				<DetailItem label="Name">{adjustment.name}</DetailItem>
				<DetailItem label="Code">{adjustment.code}</DetailItem>
				<DetailItem label="Description" fullWidth>
					{adjustment.description || "--"}
				</DetailItem>
				<DetailItem label="Type">
					{ADJUSTMENT_TYPE_LABELS[adjustment.adjustmentType] ||
						adjustment.adjustmentType}
				</DetailItem>
				<DetailItem label="Value">{formatAdjustmentValue(adjustment)}</DetailItem>
				<DetailItem label="Pay Period">
					{formatAdjustmentPayPeriod(adjustment, payPeriodCodes)}
				</DetailItem>
				<DetailItem label="Status">
					<SalaryAdjustmentStatusBadge status={adjustment.status} />
				</DetailItem>
				{upcomingProcessing && (
					<DetailItem label="What Happens Next" fullWidth>
						<p className="text-gray-600 bg-amber-50 rounded-lg px-4 py-2">
							{upcomingProcessing}
						</p>
					</DetailItem>
				)}
				<DetailItem label="Created By">{formatActor(adjustment.actor)}</DetailItem>
				<DetailItem label="Date Created">
					{formatDateAndTime(adjustment.createdAt) || "--"}
				</DetailItem>
				<DetailItem label="Applied On">
					{adjustment.appliedAt ? formatDateAndTime(adjustment.appliedAt) : "--"}
				</DetailItem>
				<DetailItem label="Date Modified">
					{adjustment.modifiedAt
						? formatDateAndTime(adjustment.modifiedAt)
						: "--"}
				</DetailItem>
				{adjustment.reversedAt && (
					<>
						<DetailItem label="Undone On">
							{formatDateAndTime(adjustment.reversedAt)}
						</DetailItem>
						<DetailItem label="Undone By">
							{formatActor(adjustment.reversedBy)}
						</DetailItem>
					</>
				)}
				<DetailItem label="Notification Recipients" fullWidth>
					{recipientNames.length ? recipientNames.join(", ") : "--"}
				</DetailItem>
				{(adjustment.status === SalaryAdjustmentStatus.FAILED_PROCESSING ||
					adjustment.status === SalaryAdjustmentStatus.EXPIRED) && (
					<DetailItem
						label={
							adjustment.status === SalaryAdjustmentStatus.EXPIRED
								? "Expiry Reason"
								: "Failure Reason"
						}
						fullWidth
					>
						<p className="text-secondary-red bg-red-50 rounded-lg px-4 py-2 whitespace-pre-line">
							{adjustment.report || "No failure reason was recorded."}
						</p>
					</DetailItem>
				)}
				{reversalReportLines.length > 0 && (
					<DetailItem label="Skipped While Undoing" fullWidth>
						<p className="text-xs text-gray-500 mb-2">
							These values had changed since the adjustment was applied, so
							they were left as they are.
						</p>
						<ul className="list-disc pl-5 space-y-1 max-h-48 overflow-y-auto bg-amber-50 rounded-lg py-2 pr-4">
							{reversalReportLines.map((line, index) => (
								<li key={index}>{line}</li>
							))}
						</ul>
					</DetailItem>
				)}
			</div>
		</ModalComponent>
	);
};

export default SalaryAdjustmentDetailsModal;
